import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Loader2 } from 'lucide-react';
import type { Brand } from '../../types';
import { SocialIcon, type SocialPlatform } from '../icons/SocialIcon';

interface LinkBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (brandId: string) => void;
  brands: Brand[];
  accountName: string;
  platform: SocialPlatform;
  isLoading?: boolean;
  loadingBrandId?: string | null;
}

export const LinkBrandModal: React.FC<LinkBrandModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  brands,
  accountName,
  platform,
  isLoading = false,
  loadingBrandId = null
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [internalLoadingBrandId, setInternalLoadingBrandId] = useState<string | null>(null);

  // Reset internal loading state when modal closes or when not loading
  React.useEffect(() => {
    if (!isOpen || !isLoading) {
      setInternalLoadingBrandId(null);
    }
  }, [isOpen, isLoading]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-xl border border-black max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Link @{accountName}</h2>
              <SocialIcon platform={platform} size={20} />
              <span className="text-lg font-semibold">to:</span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-3">
            {brands.map((brand, index) => {
              // Use external loadingBrandId if provided, otherwise use internal state
              const effectiveLoadingBrandId = loadingBrandId || internalLoadingBrandId;
              const isThisBrandLoading = effectiveLoadingBrandId === brand.id;
              const showLoader = isThisBrandLoading;
              const isDisabled = isLoading || isThisBrandLoading;
              
              return (
                <button
                  key={brand.id}
                  onClick={() => {
                    setInternalLoadingBrandId(brand.id);
                    onSelect(brand.id);
                  }}
                  disabled={isDisabled}
                  className={`flex items-center gap-2 w-full p-3 rounded-lg border border-black transition-colors ${
                    isDisabled
                      ? 'bg-gray-50 cursor-not-allowed'
                      : 'hover:border-primary-600'
                  }`}
                >
                  {showLoader ? (
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                  ) : (
                    <img 
                      src={brand.logo} 
                      alt={brand.name} 
                      className="w-6 h-6 rounded-full"
                    />
                  )}
                  <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                    {brand.name}
                  </span>
                  {showLoader && (
                    <span className="text-xs text-primary-600 ml-auto">
                      {t('common.linking', 'Linking...')}
                    </span>
                  )}
                </button>
              );
            })}

            <button
              onClick={() => {
                window.location.href = 'https://app.socialness.ai/brand-strategy-lab?modal=analyze';
              }}
              disabled={isLoading}
              className="flex items-center gap-2 w-full p-3 rounded-lg border border-dashed border-black hover:border-primary-600 text-gray-500 hover:text-primary-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span className="text-sm">Create new brand</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};